import { useState } from "react";
import useApi from "./useApi";
import usePage from "./usePage";

const useClientInvoice = () => {
  const { getData, postData } = useApi();
  const { showMessage } = usePage();

  const [invoices, setInvoices] = useState([]);
  const [currentInvoice, setCurrentInvoice] = useState(null);
  const [loading, setLoading] = useState(false);

  const getInvoices = async (filter = null) => {
    setLoading(true);
    try {
      const results = await getData({
        entityName: "ClientInvoice",
        filter: filter || {},
        pipeline: "standard",
      });

      if (results.isSuccessful) {
        const invoiceArray = results.data ? Object.values(results.data) : [];
        setInvoices(invoiceArray);
      } else {
        setInvoices([]);
        showMessage(results.message || "Error fetching invoices", "error");
      }
    } catch (err) {
      console.error("Error fetching invoices:", err);
      setInvoices([]);
      showMessage("Error fetching invoices", "error");
    } finally {
      setLoading(false);
    }
  };

  const getInvoicesByStatus = async (status) => {
    setLoading(true);
    try {
      const results = await getData({
        entityName: "ClientInvoice",
        filter: { status },
        pipeline: "standard",
      });

      if (results.isSuccessful) {
        const invoiceArray = results.data ? Object.values(results.data) : [];
        setInvoices(invoiceArray);
      } else {
        setInvoices([]);
        showMessage(results.message || "Error fetching invoices", "error");
      }
    } catch (err) {
      console.error("Error fetching invoices:", err);
      setInvoices([]);
      showMessage("Error fetching invoices", "error");
    } finally {
      setLoading(false);
    }
  };

  const getInvoiceById = async (invoiceId) => {
    setLoading(true);
    try {
      const results = await getData({
        entityName: "ClientInvoice",
        id: invoiceId,
        pipeline: "standard",
      });

      if (results.isSuccessful) {
        setCurrentInvoice(results.data);
      } else {
        setCurrentInvoice(null);
        showMessage(results.message || "Error fetching invoice", "error");
      }
    } catch (err) {
      console.error("Error fetching invoice:", err);
      setCurrentInvoice(null);
      showMessage("Error fetching invoice", "error");
    } finally {
      setLoading(false);
    }
  };

  const updateInvoice = async (invoiceId, invoiceData) => {
    setLoading(true);
    try {
      const results = await postData({
        entityName: "ClientInvoice",
        action: "update",
        data: { id: invoiceId, ...invoiceData },
      });

      if (results.isSuccessful) {
        setCurrentInvoice(results.data);
        showMessage("Invoice updated successfully", "success");
        return results.data;
      } else {
        showMessage(results.message || "Error updating invoice", "error");
        return null;
      }
    } catch (err) {
      console.error("Error updating invoice:", err);
      showMessage("Error updating invoice", "error");
      return null;
    } finally {
      setLoading(false);
    }
  };

  const approveInvoice = async (invoiceId) => {
    setLoading(true);
    try {
      const results = await postData({
        entityName: "ClientInvoice",
        action: "update",
        data: { id: invoiceId, status: "approved" },
      });

      if (results.isSuccessful) {
        setCurrentInvoice(results.data);
        showMessage("Invoice approved successfully", "success");
        return results.data;
      } else {
        showMessage(results.message || "Error approving invoice", "error");
        return null;
      }
    } catch (err) {
      console.error("Error approving invoice:", err);
      showMessage("Error approving invoice", "error");
      return null;
    } finally {
      setLoading(false);
    }
  };

  const sendInvoice = async (invoiceId) => {
    setLoading(true);
    try {
      const results = await postData({
        entityName: "ClientInvoice",
        action: "update",
        data: { id: invoiceId, status: "sent" },
      });

      if (results.isSuccessful) {
        setCurrentInvoice(results.data);
        showMessage("Invoice sent successfully", "success");
        return results.data;
      } else {
        showMessage(results.message || "Error sending invoice", "error");
        return null;
      }
    } catch (err) {
      console.error("Error sending invoice:", err);
      showMessage("Error sending invoice", "error");
      return null;
    } finally {
      setLoading(false);
    }
  };

  const generateInvoices = async (clientId, projectId, invoicePeriod) => {
    setLoading(true);
    try {
      const results = await postData({
        entityName: "ClientInvoice",
        action: "generateInvoices",
        data: { clientId, projectId, invoicePeriod },
      });

      if (results.isSuccessful) {
        if (results.data && results.data.warning) {
          // Return warning data so caller can handle confirmation
          return results.data;
        }

        setCurrentInvoice(results.data);
        showMessage("Invoice generated successfully", "success");
        return results.data;
      } else {
        showMessage(results.message || "Error generating invoice", "error");
        return null;
      }
    } catch (err) {
      console.error("Error generating invoice:", err);
      showMessage("Error generating invoice", "error");
      return null;
    } finally {
      setLoading(false);
    }
  };

  const deleteInvoice = async (invoiceId) => {
    setLoading(true);
    try {
      const results = await postData({
        entityName: "ClientInvoice",
        action: "delete",
        data: { id: invoiceId },
      });

      if (results.isSuccessful) {
        setCurrentInvoice(null);
        showMessage("Invoice deleted successfully", "success");
        return true;
      } else {
        showMessage(results.message || "Error deleting invoice", "error");
        return false;
      }
    } catch (err) {
      console.error("Error deleting invoice:", err);
      showMessage("Error deleting invoice", "error");
      return false;
    } finally {
      setLoading(false);
    }
  };

  const addLine = async (invoiceId, line) => {
    setLoading(true);
    try {
      const results = await postData({
        entityName: "ClientInvoice",
        action: "addLine",
        data: { id: invoiceId, line },
      });

      if (results.isSuccessful) {
        setCurrentInvoice(results.data);
        showMessage("Line added successfully", "success");
        return results.data;
      } else {
        showMessage(results.message || "Error adding line", "error");
        return null;
      }
    } catch (err) {
      console.error("Error adding line:", err);
      showMessage("Error adding line", "error");
      return null;
    } finally {
      setLoading(false);
    }
  };

  const updateLine = async (invoiceId, lineId, line) => {
    setLoading(true);
    try {
      const results = await postData({
        entityName: "ClientInvoice",
        action: "updateLine",
        data: { id: invoiceId, lineId, line },
      });

      if (results.isSuccessful) {
        setCurrentInvoice(results.data);
        showMessage("Line updated successfully", "success");
        return results.data;
      } else {
        showMessage(results.message || "Error updating line", "error");
        return null;
      }
    } catch (err) {
      console.error("Error updating line:", err);
      showMessage("Error updating line", "error");
      return null;
    } finally {
      setLoading(false);
    }
  };

  const deleteLine = async (invoiceId, lineId) => {
    setLoading(true);
    try {
      const results = await postData({
        entityName: "ClientInvoice",
        action: "deleteLine",
        data: { id: invoiceId, lineId },
      });

      if (results.isSuccessful) {
        setCurrentInvoice(results.data);
        showMessage("Line deleted successfully", "success");
        return results.data;
      } else {
        showMessage(results.message || "Error deleting line", "error");
        return null;
      }
    } catch (err) {
      console.error("Error deleting line:", err);
      showMessage("Error deleting line", "error");
      return null;
    } finally {
      setLoading(false);
    }
  };

  const exportCSV = async (invoiceId) => {
    setLoading(true);
    try {
      const results = await postData({
        entityName: "ClientInvoice",
        action: "exportCSV",
        data: { id: invoiceId },
      });

      if (results.isSuccessful && results.data) {
        // Create blob and download
        const blob = new Blob([results.data.csv], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = results.data.filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        showMessage("CSV downloaded successfully", "success");
        return true;
      } else {
        showMessage(results.message || "Error exporting CSV", "error");
        return false;
      }
    } catch (err) {
      console.error("Error exporting CSV:", err);
      showMessage("Error exporting CSV", "error");
      return false;
    } finally {
      setLoading(false);
    }
  };

  const generatePDF = async (invoiceId) => {
    setLoading(true);
    try {
      const results = await postData({
        entityName: "ClientInvoice",
        action: "generatePDF",
        data: { id: invoiceId },
      });

      if (results.isSuccessful && results.data) {
        // Convert base64 to binary using Uint8Array.from
        const binaryString = window.atob(results.data.pdfBuffer);
        const bytes = Uint8Array.from(binaryString, (c) => c.charCodeAt(0));
        const blob = new Blob([bytes], { type: "application/pdf" });

        // Create download link
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = results.data.filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);

        showMessage("PDF downloaded successfully", "success");
        return results.data;
      } else {
        showMessage(results.message || "Error generating PDF", "error");
        return null;
      }
    } catch (err) {
      console.error("Error generating PDF:", err);
      showMessage("Error generating PDF", "error");
      return null;
    } finally {
      setLoading(false);
    }
  };

  const getAuditHistory = async (invoiceId) => {
    setLoading(true);
    try {
      const results = await getData({
        entityName: "InvoiceAuditLog",
        filter: { invoiceId },
        pipeline: "standard",
      });

      if (results.isSuccessful) {
        const auditEntries = results.data ? Object.values(results.data) : [];
        return auditEntries.sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
        );
      } else {
        showMessage(results.message || "Error fetching audit history", "error");
        return [];
      }
    } catch (err) {
      console.error("Error fetching audit history:", err);
      showMessage("Error fetching audit history", "error");
      return [];
    } finally {
      setLoading(false);
    }
  };

  const resendInvoice = async (invoiceId) => {
    setLoading(true);
    try {
      const results = await postData({
        entityName: "ClientInvoice",
        action: "resendInvoice",
        data: { id: invoiceId },
      });

      if (results.isSuccessful) {
        setCurrentInvoice(results.data);
        showMessage("Invoice resent successfully", "success");
        return true;
      } else {
        showMessage(results.message || "Error resending invoice", "error");
        return false;
      }
    } catch (err) {
      console.error("Error resending invoice:", err);
      showMessage("Error resending invoice", "error");
      return false;
    } finally {
      setLoading(false);
    }
  };

  return {
    invoices,
    currentInvoice,
    loading,
    getInvoices,
    getInvoicesByStatus,
    getInvoiceById,
    updateInvoice,
    approveInvoice,
    sendInvoice,
    generateInvoices,
    deleteInvoice,
    addLine,
    updateLine,
    deleteLine,
    exportCSV,
    generatePDF,
    getAuditHistory,
    resendInvoice,
  };
};

export default useClientInvoice;
